// frontend/packages/business-console/src/pages/Scheduling/TimeOffPanel.jsx
//
// Time-off CRUD for one staff member. Time off is stored as UTC instants
// (startUtc/endUtc) plus an optional reason; the editor uses native
// <input type="datetime-local"> (local wall clock) and converts to UTC ISO on
// save. Config writes are businessadmin/manager server-side (F.10).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import {
  listTimeOff,
  createTimeOff,
  deleteTimeOff,
} from '../../services/schedulingService';
import { Surface, useTokens } from '../../components/primitives';
import { formatDateTime, localInputToUtcIso } from './helpers';

function serverMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function TimeOffPanel({ staff }) {
  const t = useTokens();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Draft for a new entry.
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!staff?._id) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listTimeOff(staff._id);
      setItems(rows);
    } catch (err) {
      setError(serverMessage(err, "Couldn't load time off."));
    } finally {
      setLoading(false);
    }
  }, [staff?._id]);

  useEffect(() => {
    load();
  }, [load]);

  const resetDraft = () => {
    setStart('');
    setEnd('');
    setReason('');
    setAdding(false);
  };

  const save = async () => {
    if (busy) return;
    const startUtc = localInputToUtcIso(start);
    const endUtc = localInputToUtcIso(end);
    if (!startUtc || !endUtc) {
      setError('Pick a start and end.');
      return;
    }
    if (new Date(endUtc) <= new Date(startUtc)) {
      setError('End must be after start.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createTimeOff({
        staffId: staff._id,
        startUtc,
        endUtc,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      resetDraft();
      load();
    } catch (err) {
      setError(serverMessage(err, "Couldn't add time off."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteTimeOff(id);
      load();
    } catch (err) {
      setError(serverMessage(err, "Couldn't remove time off."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Time off
        </Typography>
        {!adding ? (
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setAdding(true)}
            sx={{ textTransform: 'none' }}
          >
            Add time off
          </Button>
        ) : null}
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {adding ? (
        <Box
          sx={{
            border: `1px solid ${t.cardBorder}`,
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Stack spacing={1.5}>
            <TextField
              type="datetime-local"
              size="small"
              label="Start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={busy}
            />
            <TextField
              type="datetime-local"
              size="small"
              label="End"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={busy}
            />
            <TextField
              size="small"
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={resetDraft} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={save}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} /> : null}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          No time off scheduled.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {items.map((o) => (
            <Box
              key={o._id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {formatDateTime(o.startUtc)} to {formatDateTime(o.endUtc)}
                </Typography>
                {o.reason ? (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {o.reason}
                  </Typography>
                ) : null}
              </Box>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => remove(o._id)}
                  aria-label="Remove time off"
                  disabled={busy}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
    </Surface>
  );
}
